import { ChevronDown, Plus, Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { CredentialKind } from "../api/CrmApi";
import { CREDENTIAL_KIND_LABELS, credentialNameSuggestions } from "../credentialNames";

interface Props {
  kinds: CredentialKind[];
  /** `name` is null for "Other…" (a blank row to type into). */
  onPick: (kind: CredentialKind, name: string | null) => void;
  /** Lower-cased `kind:name` of credentials already in the list, hidden from the menu. */
  taken: Set<string>;
  disabled?: boolean;
}

interface Option { kind: CredentialKind; name: string | null; label: string }
interface Group { title: string; options: Option[] }

/** One "Add credential" dropdown: every suggested name grouped by kind, searchable and keyboard-operable (↑ ↓ Enter Esc).
 *  Typing a name that is not in the list offers "Add “…” as <kind>", so a custom credential can be added from a search. */
export default function CredentialPicker({ kinds, onPick, taken, disabled }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const needle = query.trim().toLowerCase();
  const typed = query.trim();
  const groups: Group[] = [];
  for (const kind of kinds) {
    const options: Option[] = credentialNameSuggestions(kind)
      .filter((n) => n.toLowerCase().includes(needle) && !taken.has(`${kind}:${n.toLowerCase()}`))
      .map((n) => ({ kind, name: n, label: n }));
    if (needle === "") options.push({ kind, name: null, label: "Other…" });
    if (options.length) groups.push({ title: CREDENTIAL_KIND_LABELS[kind], options });
  }
  if (typed) {
    groups.push({ title: `Add “${typed}” as`, options: kinds.map((kind) => ({ kind, name: typed, label: CREDENTIAL_KIND_LABELS[kind] })) });
  }
  const flat = groups.flatMap((g) => g.options);

  const close = () => { setOpen(false); setQuery(""); setActive(0); };
  const pick = (o: Option) => { onPick(o.kind, o.name); close(); };

  return (
    <div
      ref={box}
      className="relative"
      onKeyDown={(e) => { if (e.key === "Escape" && open) { e.stopPropagation(); close(); } }}
      onBlur={(e) => { if (open && !box.current?.contains(e.relatedTarget as Node | null)) close(); }}
    >
      <button
        type="button"
        disabled={disabled}
        onClick={() => (open ? close() : setOpen(true))}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex w-full items-center justify-between rounded-xl border border-border bg-surface-muted px-3 py-2 text-sm text-foreground hover:border-muted-foreground focus:outline-none focus:border-primary disabled:opacity-50"
      >
        <span className="flex items-center gap-1.5"><Plus className="h-4 w-4" /> Add credential</span>
        <ChevronDown className="h-4 w-4 text-muted-foreground" />
      </button>
      {open && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 rounded-xl border border-border bg-surface shadow-lg">
          <div className="flex items-center gap-2 border-b border-border px-3 py-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input
              autoFocus
              value={query}
              onChange={(e) => { setQuery(e.target.value); setActive(0); }}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, flat.length - 1)); }
                else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
                else if (e.key === "Enter") { e.preventDefault(); if (flat[active]) pick(flat[active]); }
              }}
              placeholder="Search, or type a new name…"
              aria-label="Search credentials"
              className="w-full bg-transparent text-sm text-foreground placeholder-muted-foreground focus:outline-none"
            />
          </div>
          <ul role="listbox" onMouseDown={(e) => e.preventDefault()} className="max-h-72 overflow-auto py-1">
            {groups.map((g) => (
              <li key={g.title} role="presentation">
                <div className="px-3 pb-0.5 pt-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{g.title}</div>
                <ul role="group" aria-label={g.title}>
                  {g.options.map((o) => {
                    const i = flat.indexOf(o);
                    return (
                      <li
                        key={o.label}
                        role="option"
                        aria-selected={i === active}
                        ref={(el) => { if (el && i === active) el.scrollIntoView?.({ block: "nearest" }); }}
                        onMouseEnter={() => setActive(i)}
                        onClick={() => pick(o)}
                        className={`cursor-pointer px-3 py-1.5 text-sm ${o.name === null ? "text-muted-foreground" : "text-foreground"} ${i === active ? "bg-muted" : ""}`}
                      >
                        {o.label}
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
